import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useFetchWithMsal from "./useFetchWithMsal";
import { protectedResources } from "../authConfig";
import { useToast } from "../contexts/ToastContext";

export const useUserRoles = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleValue, setRoleValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [groups, setGroups] = useState([]);
  
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const fetchInvoiceUsers = async () => {
    try {
      const response = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/invoice-roles/users`
      );
      return response || [];
    } catch (error) {
      console.error('Error fetching invoice users:', error);
      throw error;
    }
  };

  const updateUserRole = async ({ userId, role, groupId }) => {
    const response = await execute(
      "PUT",
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/invoice-roles/update`,
      { userId, role, groupId }
    );
    return response;
  };

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['invoiceUsers'],
    queryFn: fetchInvoiceUsers
  });

  const updateMutation = useMutation({
    mutationFn: updateUserRole,
    onSuccess: () => {
      showToast("תפקיד המשתמש עודכן בהצלחה", "success");
      const event = new CustomEvent('permissions-changed', { 
        detail: { userId: selectedUser?._id }
      });
      window.dispatchEvent(event);

      queryClient.invalidateQueries(['invoiceUsers']);
      handleDialogClose();
    },
    onError: (error) => {
      showToast("שגיאה בעדכון תפקיד המשתמש", "error");
      console.error('Error updating user role:', error);
    }
  });

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/groups`);
        const data = await res.json();
        setGroups(data);
      } catch (err) {
        setGroups([]);
      }
    };
    fetchGroups();
  }, []);

  const handleDialogOpen = (user) => {
    setSelectedUser(user);
    setRoleValue(user.invoiceRole || 'regular');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleSubmit = () => {
    updateMutation.mutate(
      { userId: selectedUser._id, role: roleValue, groupId: selectedUser.groupId || null },
      {
        onSuccess: () => {
          window.dispatchEvent(new CustomEvent('permissions-changed'));
        }
      }
    );
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.Name?.toLowerCase().includes(searchLower) ||
      user.LastName?.toLowerCase().includes(searchLower) ||
      user.Email?.toLowerCase().includes(searchLower)
    );
  });

  const getRoleDisplay = (role) => {
    switch(role) {
      case 'approver': return 'מאשר';
      case 'accountManager': return 'מנהל/ת חשבונות';
      case 'regular': return 'ברירת מחדל';
      default: return 'ברירת מחדל';
    }
  };

  return {
    dialogOpen,
    selectedUser,
    roleValue,
    searchTerm,
    users: filteredUsers,
    isLoading,
    updateMutation,
    groups,
    setSelectedUser, 
    setSearchTerm,
    setRoleValue,
    handleDialogOpen,
    handleDialogClose,
    handleSubmit,
    getRoleDisplay
  };
};